/**
 * Single source of truth for constructing ONE body's render group from a
 * manifest entry. Used by BOTH the game (render/bodies.ts) and the asset viewer
 * (viewer.ts) so they render identically. No floating-origin / world logic here
 * — just "give me the THREE.Group for this body".
 */
import * as THREE from 'three';
import { createGltfLoader } from './gltf.js';
import type { BodyManifestEntry, BodyTextureSet } from '@blackmarble/sim-core';
import { buildTexturedSphere, buildEarth, buildEarthShells } from './planetMaterials.js';

const loader = createGltfLoader();
const GAS_GIANTS = new Set(['Jupiter', 'Saturn', 'Uranus', 'Neptune']);

/** The built visual for one body (atmosphere/clouds present only for Earth). */
export interface BuiltBody {
  group: THREE.Group;
  clouds?: THREE.Mesh;
  atmosphere?: THREE.Mesh;
  atmosphereRadiusKm?: number;
  texMesh?: THREE.Mesh; // textured sphere eligible for lazy hi-res upgrade
}

/**
 * Scale a loaded GLB so its SURFACE radius == radiusKm, and centre it.
 * Uses the max box HALF-EXTENT (true radius for a centred sphere), NOT
 * Box3.getBoundingSphere() whose radius is the √3× half-diagonal.
 */
export const normaliseToRadius = (obj: THREE.Object3D, radiusKm: number): void => {
  const box = new THREE.Box3().setFromObject(obj);
  const size = box.getSize(new THREE.Vector3());
  const srcR = Math.max(size.x, size.y, size.z) / 2 || 1;
  const factor = radiusKm / srcR;
  const center = box.getCenter(new THREE.Vector3());
  obj.scale.setScalar(factor);
  obj.position.sub(center.multiplyScalar(factor));
};

/**
 * WAVE 2a: opt a crafted object (ship/station/derelict/weapon) into the sun
 * shadow map — every mesh both casts and receives so matte hulls self-shadow and
 * throw onto their neighbours. Bodies and belt instances are deliberately NOT
 * enrolled (they sit thousands of km outside the bubble ortho frustum, and
 * per-instance belt shadows would blow the perf budget).
 */
export const enableShadows = (obj: THREE.Object3D): void => {
  obj.traverse((o) => {
    if (!(o instanceof THREE.Mesh)) return;
    o.castShadow = true;
    o.receiveShadow = true;
  });
};

const tintGasGiant = (group: THREE.Group): void => {
  group.traverse((o) => {
    if (o instanceof THREE.Mesh && o.material instanceof THREE.MeshStandardMaterial) {
      o.material.emissive = new THREE.Color(0x050505);
      o.material.emissiveIntensity = 0.02;
    }
  });
};

const makeCoronaTexture = (): THREE.Texture => {
  const size = 256;
  const c = document.createElement('canvas');
  c.width = c.height = size;
  const ctx = c.getContext('2d');
  if (ctx) {
    const g = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
    g.addColorStop(0.0, 'rgba(255,240,210,0.9)');
    g.addColorStop(0.2, 'rgba(255,220,160,0.5)');
    g.addColorStop(0.5, 'rgba(255,180,90,0.15)');
    g.addColorStop(1.0, 'rgba(255,160,60,0.0)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, size, size);
  }
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
};

export const makeSolEmissive = (group: THREE.Group, radiusKm: number): void => {
  const mat = new THREE.MeshBasicMaterial({ color: 0xffd9a0 });
  mat.color.multiplyScalar(2.2);
  mat.toneMapped = false;
  group.traverse((o) => {
    if (o instanceof THREE.Mesh) o.material = mat;
  });
  const corona = new THREE.Sprite(
    new THREE.SpriteMaterial({
      map: makeCoronaTexture(),
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      transparent: true,
      toneMapped: false,
    }),
  );
  corona.scale.setScalar(radiusKm * 5);
  group.add(corona);
};

const loadGlbInto = async (
  group: THREE.Group,
  glb: string,
  radiusKm: number,
  baseUrl: string,
): Promise<void> => {
  try {
    const gltf = await loader.loadAsync(baseUrl + glb);
    normaliseToRadius(gltf.scene, radiusKm);
    group.add(gltf.scene);
  } catch {
    const geo = new THREE.SphereGeometry(radiusKm, 32, 16);
    const mat = new THREE.MeshStandardMaterial({ color: 0x888888, roughness: 0.9 });
    group.add(new THREE.Mesh(geo, mat));
  }
};

/** Load a Blender-sculpted GLB + apply normalMap + (Earth) night-lights. */
const loadSculptInto = async (
  group: THREE.Group,
  entry: BodyManifestEntry,
  tex: BodyTextureSet,
  baseUrl: string,
): Promise<void> => {
  let gltf;
  try {
    gltf = await loader.loadAsync(baseUrl + (tex.sculptGlb as string));
  } catch (err) {
    console.warn(`[assets] sculpt GLB missing (${tex.sculptGlb}) — using grey sphere`, err);
    const geo = new THREE.SphereGeometry(entry.radiusKm, 32, 16);
    const mat = new THREE.MeshStandardMaterial({ color: 0x888888, roughness: 0.9 });
    group.add(new THREE.Mesh(geo, mat));
    return;
  }
  const root = gltf.scene;
  normaliseToRadius(root, entry.radiusKm);
  root.traverse((o) => {
    if (!(o instanceof THREE.Mesh)) return;
    const mat = o.material;
    if (!(mat instanceof THREE.MeshStandardMaterial)) return;
    if (mat.map) mat.map.colorSpace = THREE.SRGBColorSpace;
    if (tex.normal) {
      mat.normalMap = new THREE.TextureLoader().load(baseUrl + tex.normal);
      mat.normalScale = new THREE.Vector2(0.6, 0.6);
    }
    if (tex.night) {
      const n = new THREE.TextureLoader().load(baseUrl + tex.night);
      n.colorSpace = THREE.SRGBColorSpace;
      mat.emissiveMap = n;
      mat.emissive = new THREE.Color(0xffffaa);
      mat.emissiveIntensity = 0.6;
    }
    mat.needsUpdate = true;
  });
  group.add(root);
};

/**
 * Build the render group for one body — the SHARED path for game + viewer.
 */
export const buildBodyGroup = async (
  entry: BodyManifestEntry,
  baseUrl = '/assets/bodies/',
): Promise<BuiltBody> => {
  const group = new THREE.Group();
  group.name = entry.name;
  const tex = entry.tex;

  if (entry.name === 'Sol') {
    await loadGlbInto(group, entry.glb, entry.radiusKm, baseUrl);
    makeSolEmissive(group, entry.radiusKm);
    return { group };
  }
  if (entry.name === 'Earth' && tex) {
    let built;
    if (tex.sculptGlb) {
      await loadSculptInto(group, entry, tex, baseUrl);
      built = buildEarthShells(entry.radiusKm, tex);
    } else {
      built = buildEarth(entry.radiusKm, tex);
    }
    group.add(built.group);
    const out: BuiltBody = { group, atmosphere: built.atmosphere, atmosphereRadiusKm: built.atmosphereRadiusKm };
    if (built.clouds) out.clouds = built.clouds;
    return out;
  }
  if (tex?.sculptGlb) {
    await loadSculptInto(group, entry, tex, baseUrl);
    return { group };
  }
  if (tex) {
    const mesh = buildTexturedSphere(entry.radiusKm, tex);
    group.add(mesh);
    return { group, texMesh: mesh };
  }
  // Saturn (and any ring-bearing original export) already contains its rings in
  // its own GLB — we do NOT attach a separate ring (that caused the duplicate
  // offset "orbiting" ring). saturn.glb bundles Rings_Mesh + Saturn at their
  // original Blender placement, so it stands as designed.
  await loadGlbInto(group, entry.glb, entry.radiusKm, baseUrl);
  if (GAS_GIANTS.has(entry.name)) tintGasGiant(group);
  return { group };
};
